import { Button, IconButton, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { HelpFlag, isHelpFeatureEnabled } from "src/config/posthog";
import { useHelpDrawer } from "./HelpDrawerContext";

interface PageTopHelpButtonProps {
  /** Display name of the product, e.g. "AtomAssist". */
  productLabel: string;
  /** Product slug used for FAQ lookup. Defaults to current route's product. */
  product?: string;
  /** Icon-only rendering for dense toolbars (label moves into a tooltip). */
  compact?: boolean;
}

/**
 * "How to use X" button placed near the top of key product pages.
 * Opens the help drawer scoped to the given product.
 */
const PageTopHelpButton = ({
  productLabel,
  product,
  compact = false,
}: PageTopHelpButtonProps) => {
  const { openDrawer } = useHelpDrawer();
  if (!isHelpFeatureEnabled(HelpFlag.HELP_DRAWER)) return null;

  const open = () => openDrawer({ entryPoint: "page_top_button", product });

  if (compact) {
    return (
      <Tooltip
        title={`How to use ${productLabel}`}
        placement="bottom"
        enterDelay={300}
      >
        <IconButton
          size="small"
          onClick={open}
          aria-label={`How to use ${productLabel}`}
          sx={{
            color: "text.secondary",
            "&:hover": { color: "primary.main", bgcolor: "action.hover" },
            "& svg": { fontSize: 20 },
          }}
        >
          <HelpOutlineIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<HelpOutlineIcon />}
      onClick={open}
    >
      How to use {productLabel}
    </Button>
  );
};

export default PageTopHelpButton;
